import React, { useEffect, useRef, useState } from 'react';
import { MatrixSlice, RegisterViewKey } from '../../hooks/useRegisterQueries';
import { SavedView } from '../../lib/registerViews';
import { cn } from '../../lib/cn';

const BUILT_IN: Array<{ key: RegisterViewKey; label: string }> = [
  { key: 'all', label: 'All risks' },
  { key: 'mine', label: 'My risks' },
  { key: 'critical-high', label: 'Critical & High' },
  { key: 'review-overdue', label: 'Review overdue' },
  { key: 'archived', label: 'Archived' },
];

export interface RegisterViewsProps {
  active: RegisterViewKey;
  activeSavedId: string | null;
  savedViews: SavedView[];
  matrix?: MatrixSlice;
  search: string;
  onSelectView: (view: RegisterViewKey) => void;
  onSelectSaved: (view: SavedView) => void;
  onSaveView: (name: string) => void;
  onDeleteSaved: (id: string) => void;
  onClearMatrix: () => void;
  onSearchChange: (search: string) => void;
}

const tabClass = (on: boolean) =>
  cn(
    'mr-4 cursor-pointer pb-0.5 text-scale-1',
    on ? 'border-b-2 border-accent font-semibold text-accent' : 'text-ink-muted hover:text-ink',
  );

export const RegisterViews: React.FC<RegisterViewsProps> = (props) => {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [term, setTerm] = useState(props.search);
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastEmitted = useRef(props.search);

  useEffect(() => () => clearTimeout(debounce.current), []);

  // Sync external search changes (saved-view selection, view reset) into the
  // input, but ignore the parent echoing back a value this input just emitted
  // so in-progress typing is never clobbered.
  useEffect(() => {
    if (props.search !== lastEmitted.current) {
      clearTimeout(debounce.current);
      setTerm(props.search);
      lastEmitted.current = props.search;
    }
  }, [props.search]);

  const submitName = () => {
    if (name.trim()) props.onSaveView(name.trim());
    setNaming(false);
    setName('');
  };

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-y-2 pt-2">
      <div className="flex flex-wrap items-baseline">
        {BUILT_IN.map((tab) => {
          const on = props.active === tab.key && props.activeSavedId == null;
          return (
            <button key={tab.key} type="button" onClick={() => props.onSelectView(tab.key)}
              aria-current={on ? 'true' : undefined} className={tabClass(on)}>
              {tab.label}
            </button>
          );
        })}
        {props.savedViews.map((view) => (
          <span key={view.id} className="mr-4 inline-flex items-baseline">
            <button type="button" onClick={() => props.onSelectSaved(view)}
              aria-current={props.activeSavedId === view.id ? 'true' : undefined}
              className={cn(tabClass(props.activeSavedId === view.id), 'mr-1')}>
              {view.name}
            </button>
            <button type="button" aria-label={`Delete view ${view.name}`}
              onClick={() => props.onDeleteSaved(view.id)}
              className="cursor-pointer text-scale-0 text-ink-muted hover:text-accent">
              ✕
            </button>
          </span>
        ))}
        {naming ? (
          <input
            autoFocus
            value={name}
            placeholder="View name"
            aria-label="View name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitName();
              if (e.key === 'Escape') { setNaming(false); setName(''); }
            }}
            onBlur={() => { setNaming(false); setName(''); }}
            className="w-28 border-b border-ink bg-transparent text-scale-1 text-ink outline-none"
          />
        ) : (
          <button type="button" onClick={() => setNaming(true)} className="cursor-pointer text-scale-1 text-ink-muted/60 hover:text-ink">
            + Save view
          </button>
        )}
        {props.matrix && (
          <span className="ml-2 inline-flex items-baseline gap-1 border border-rule-faint px-2 py-0.5">
            <span className="font-mono text-scale-0 text-ink">
              Matrix · {props.matrix.type === 'residual' ? 'R' : 'I'} {props.matrix.likelihood}×{props.matrix.impact}
            </span>
            <button type="button" aria-label="Clear matrix filter" onClick={props.onClearMatrix}
              className="cursor-pointer text-scale-0 text-ink-muted hover:text-accent">
              ✕
            </button>
          </span>
        )}
      </div>
      <input
        value={term}
        placeholder="Search risks…"
        onChange={(e) => {
          const { value } = e.target;
          setTerm(value);
          clearTimeout(debounce.current);
          debounce.current = setTimeout(() => {
            lastEmitted.current = value;
            props.onSearchChange(value);
          }, 300);
        }}
        className="w-44 border-b border-rule-faint bg-transparent text-scale-1 text-ink outline-none placeholder:text-ink-muted/60 focus:border-accent"
        aria-label="Search risks"
      />
    </div>
  );
};
